import { ChangeDetectionStrategy, Component, EventEmitter, OnChanges, OnInit, Output, input, model, output } from '@angular/core';
import { FormArray, FormBuilder, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog'
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { faWarning } from '@fortawesome/free-solid-svg-icons';
import { faCircleXmark } from '@fortawesome/free-regular-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { ReportService } from 'src/app/services/report.service';
import { Editor, Toolbar } from 'ngx-editor';
import { Subject } from 'rxjs';
import { ConfirmationToastComponent } from 'src/app/components/confirmation-toast/confirmation-toast.component';
import { StateCounty } from 'src/app/models/stateCounty.model';
import { APP_CONSTANTS } from 'src/app/shared/app.constants';
import { AutoSaveComponent } from '../auto-save/auto-save.component';

@Component({
  selector: 'app-watershed-condition',
  templateUrl: './watershed-condition.component.html',
  styleUrls: ['./watershed-condition.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})


export class WatershedConditionComponent extends AutoSaveComponent implements OnChanges, OnInit {

  faWarning = faWarning;
  faCircleXmark = faCircleXmark as IconProp;

  editor0!: Editor;
  editor1!: Editor;
  editor2!: Editor;
  editor3!: Editor;
  editor4!: Editor;
  toolbar: Toolbar = APP_CONSTANTS.EDITOR_TOOLBAR;

  report = input.required<any>();
  nav = model.required<string>();
  status = output<number | undefined>();
  @Output() loaded = new EventEmitter();
  errorList: string[] = [];
  forests = []
  saveSubject = new Subject<any>();

  optional = input(false);

  form = this.formBuilder.group({
    acresBurned: this.formBuilder.array([]),
    burn_severity_description: ["", Validators.maxLength(6000)],
    erosion_potential: ["", Validators.maxLength(6000)],
    debris_potential: ["", Validators.maxLength(6000)],
    vegetative_recovery: ["", Validators.maxLength(6000)],
    hydrolic_response: ["", Validators.maxLength(6000)],

  });
  get acresBurned() {
    return this.form.get("acresBurned") as FormArray;
  }

  constructor(
    public dialog: MatDialog,
    private readonly titleService: Title,
    private readonly router: Router,
    private readonly formBuilder: FormBuilder,
    public readonly reportService: ReportService,
    public readonly toast: ConfirmationToastComponent,
  ) {
    super()
  }

  ngOnInit(): void {
    this.status.emit(undefined);
    this.editor0 = new Editor();
    this.editor1 = new Editor();
    this.editor2 = new Editor();
    this.editor3 = new Editor();
    this.editor4 = new Editor();
    this.forests = this.report().assessment.forest_name.split(",")
    this.buildTableForms()
    this.form.patchValue({
      burn_severity_description: this.customATOB(this.report().burn_severity_description ?? ""),
      erosion_potential: this.customATOB(this.report().erosion_potential ?? ""),
      debris_potential: this.customATOB(this.report().debris_potential ?? ""),
      vegetative_recovery: this.customATOB(this.report().vegetative_recovery ?? ""),
      hydrolic_response: this.customATOB(this.report().hydrolic_response ?? ""),
    })
    this.listenForChanges(this.form);
    this.formReady = true;
    this.loaded.emit(true);
  }

  onChangeEditor(html: any, field: any) {
    if (html == '<p></p>') {
      let patch = '{"' + field + '": ""}'
      this.form.patchValue(JSON.parse(patch))
    }
  }

  buildTableForms(): void {
    let states = this.report().stateCounties.filter((state: StateCounty) => state?.state_name != null).map((state: StateCounty) => state?.state_name)
    states = [...new Set(states)]; //remove duplicates

    let burnData = [
      {
        ownership: "Private",
        acres_burned: 0,
        percent_burned: 0,
        burn_acres_developed: 0,
        burn_acres_unburned: 0,
        burn_acres_low: 0,
        burn_acres_moderate: 0,
        burn_acres_high: 0,
        report_soil_burn_severity_id: -1
      },
      {
        ownership: "Other Federal",
        acres_burned: 0,
        percent_burned: 0,
        burn_acres_developed: 0,
        burn_acres_unburned: 0,
        burn_acres_low: 0,
        burn_acres_moderate: 0,
        burn_acres_high: 0,
        report_soil_burn_severity_id: -1
      },
    ]

    for (let state of states) {
      burnData.unshift(
        {
          ownership: "State of " + state,
          acres_burned: 0,
          percent_burned: 0,
          burn_acres_developed: 0,
          burn_acres_unburned: 0,
          burn_acres_low: 0,
          burn_acres_moderate: 0,
          burn_acres_high: 0,
          report_soil_burn_severity_id: -1
        }
      )
    }

    for (let forest of this.forests) {
      burnData.unshift(
        {
          ownership: forest,
          acres_burned: 0,
          percent_burned: 0,
          burn_acres_developed: 0,
          burn_acres_unburned: 0,
          burn_acres_low: 0,
          burn_acres_moderate: 0,
          burn_acres_high: 0,
          report_soil_burn_severity_id: -1
        }
      )
    }

    for (let item of this.report().soil_burn_severities) {
      let burnDataItem = burnData.find((x: any) => x.ownership == item.ownership)
      if (burnDataItem) {
        burnDataItem.acres_burned = item.acres_burned;
        burnDataItem.percent_burned = item.percent_burned;
        burnDataItem.burn_acres_developed = item.burn_acres_developed;
        burnDataItem.burn_acres_unburned = item.burn_acres_unburned;
        burnDataItem.burn_acres_low = item.burn_acres_low;
        burnDataItem.burn_acres_moderate = item.burn_acres_moderate;
        burnDataItem.burn_acres_high = item.burn_acres_high;
        burnDataItem.report_soil_burn_severity_id = item.report_soil_burn_severity_id;
      }
    }

    for (let data of burnData) {
      if (data.report_soil_burn_severity_id == -1) {
        this.reportService.createBurnSeverity({
          report_id: this.report().report_id,
          ownership: data.ownership,
          acres_burned: 0,
          percent_burned: 0,
          burn_acres_developed: 0,
          burn_acres_unburned: 0,
          burn_acres_low: 0,
          burn_acres_moderate: 0,
          burn_acres_high: 0
        }).subscribe((response: any) => {
          this.acresBurned.push(this.formBuilder.group({
            ownership: data.ownership,
            acresBurned: data.acres_burned,
            percentBurned: data.percent_burned,
            burn_acres_developed: data.burn_acres_developed,
            burn_acres_unburned: data.burn_acres_unburned,
            burn_acres_low: data.burn_acres_low,
            burn_acres_moderate: data.burn_acres_moderate,
            burn_acres_high: data.burn_acres_high,
            report_soil_burn_severity_id: response.report_soil_burn_severity_id
          }))
        })
      }
      else {
        this.acresBurned.push(this.formBuilder.group({
          ownership: data.ownership,
          acres_burned: data.acres_burned,
          percent_burned: data.percent_burned,
          burn_acres_developed: data.burn_acres_developed,
          burn_acres_unburned: data.burn_acres_unburned,
          burn_acres_low: data.burn_acres_low,
          burn_acres_moderate: data.burn_acres_moderate,
          burn_acres_high: data.burn_acres_high,
          report_soil_burn_severity_id: data.report_soil_burn_severity_id,
        }))
      }
    }
  }

  ngOnChanges(): void {
    if (this.nav() != '') this.navigate();
  }

  navigate() {
    this.save(true);
  }

  route() {
    switch (this.nav()) {
      case 'forward': {
        this.router.navigate(['/2500-8-report'], { queryParams: { report_id: this.report().report_id, report_page_input: 4 } });
        break;
      }
      case 'back': {
        this.router.navigate(['/2500-8-report'], { queryParams: { report_id: this.report().report_id, report_page_input: 2 } });
        break;
      }
      case 'close': {
        this.router.navigate(['fire-overview'], { queryParams: { assessment_id: this.report().assessment_id } });
        break;
      }
      default: {
        if (Number(this.nav()))
          this.router.navigate(['/2500-8-report'], { queryParams: { report_id: this.report().report_id, report_page_input: this.nav() } });
        break;
      }
    }
  }

  gatherData(): any {
    let data: any = {
      burn_severity_description: this.form.controls.burn_severity_description.valid ? this.customBTOA(this.form.value.burn_severity_description as string) : null,
      erosion_potential: this.form.controls.erosion_potential.valid ? this.customBTOA(this.form.value.erosion_potential as string) : null,
      debris_potential: this.form.controls.debris_potential.valid ? this.customBTOA(this.form.value.debris_potential as string) : null,
      vegetative_recovery: this.form.controls.vegetative_recovery.valid ? this.customBTOA(this.form.value.vegetative_recovery as string) : null,
      hydrolic_response: this.form.controls.hydrolic_response.valid ? this.customBTOA(this.form.value.hydrolic_response as string) : null,
    }
    let soils = this.acresBurned.value.map((item: any) => {
      let data = {
        report_id: this.report().report_id,
        report_soil_burn_severity_id: item.report_soil_burn_severity_id == -1 ? null : item.report_soil_burn_severity_id,
        ownership: item.ownership,
        acres_burned: +item.acres_burned,
        percent_burned: +item.percent_burned,
        burn_acres_developed: +item.burn_acres_developed,
        burn_acres_unburned: +item.burn_acres_unburned,
        burn_acres_low: +item.burn_acres_low,
        burn_acres_moderate: +item.burn_acres_moderate,
        burn_acres_high: +item.burn_acres_high,
      }
      return data
    })

    let allFieldsFilled = true
    if (this.acresBurned.invalid) allFieldsFilled = false;

    let fields = ["burn_severity_description", "erosion_potential", "debris_potential", "vegetative_recovery", "hydrolic_response"]

    for (let field of fields) {
      if (this.form.get(field)?.value === "") {
        allFieldsFilled = false
      }
    }

    if (this.optional()) allFieldsFilled = true

    data.allFieldsFilled = allFieldsFilled
    data.soils = soils

    return data
  }

  override save(navigate?: boolean) {
    this.navDebounceCheck(navigate)

    if (!this.form.dirty) {
      if (navigate) {
        this.route()
      }
      // Don't save if nothing has changed on autosave
      return
    }

    let data = this.gatherData()

    if (!navigate && this.checkIfNotNeedToSave(data)) return

    this.saveSubject.next(null);
    this.status.emit(data.allFieldsFilled ? 2 : 1);

    this.reportService.updateReport({
      report_id: this.report().report_id,

      soil_burn_severities: data.soils,
      burn_severity_description: data.burn_severity_description,
      erosion_potential: data.erosion_potential,
      debris_potential: data.debris_potential,
      vegetative_recovery: data.vegetative_recovery,
      hydrolic_response: data.hydrolic_response,
      page_3_status_id: data.allFieldsFilled ? 2 : 1,
    }).subscribe({
      next: (response) => {
        if (navigate) {
          if (this.nav() == "forward" && !data.allFieldsFilled) {
            this.form.markAllAsTouched();
            this.errorList = [];
            for (let field in this.form.controls) {
              const control = this.form.get(field);
              if (control?.invalid) {
                this.errorList.push(field.replaceAll('_', ' '));
              }
            }
            window.scrollTo({ top: 0, behavior: 'smooth' });
            this.nav.set('');
            this.postSave();
          } else
            this.route() // navigate route after save because next page needs to see this page's changes
        } else {
          this.postSave()
        }
      },
      error: (response) => {
        this.toast.openErrorSnackBar(`There was an error saving the report`, "Please try again.");
      },
    })
  }

  sumAcresBurned(level: string, sbs: boolean) {
    let total = 0;
    this.acresBurned.value.forEach((item: any, index: number) => {
      if (!sbs || (!item.ownership.includes('State') && index != (this.acresBurned.value?.length - 2) && index != (this.acresBurned.value?.length - 1)))
        total += +item[level];
    })
    return total;
  }

  categoryTotal(ownership: string) {
    let total = 0;
    this.acresBurned.value.forEach((item: any, index: number) => {
      if ((ownership == 'nfs' && index != (this.acresBurned.value?.length - 2) && index != (this.acresBurned.value?.length - 1))
        || item.ownership.includes(ownership)
        || ownership == 'total')
        total = total + Number(item['burn_acres_developed'] ?? 0) + item['burn_acres_unburned'] + item['burn_acres_low'] + item['burn_acres_moderate'] + item['burn_acres_high'];
    })
    return total;
  }

  customATOB(s: string): string {
    try {
      return atob(s)
    } catch (err) {
      if (err) {
        return s
      }
    }
    return s
  }

  customBTOA(s: string): string {
    return btoa(s.replaceAll(/[\u00A0-\u2666]/g, function (c) {
      return '&#' + c.codePointAt(0) + ';';
    }));
  }
}
